'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Topbar from '@/components/layout/Topbar';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const staffingServices = [
{
  id: 'india',
  title: 'IT Staffing in India',
  description: 'Access India\'s vast pool of 5 million+ highly skilled IT professionals. We connect you with top-tier software engineers, AI/ML specialists, data scientists, cloud architects, and more — rigorously vetted and ready to contribute from day one at highly competitive rates.',
  features: ['Software Engineers & Full-Stack Developers', 'AI/ML Engineers & Data Scientists', 'Cloud & DevOps Specialists', 'QA & Testing Professionals', 'Project Managers & Scrum Masters', 'UI/UX Designers & Product Managers'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c584ce76-1781242296348.png",
  alt: 'Indian software development team collaborating in a modern tech office with multiple monitors showing AI and cloud projects'
},
{
  id: 'usa',
  title: 'IT Staffing in USA',
  description: 'Find the right IT talent across the United States. Whether you need local on-site expertise or remote professionals, we source, screen, and place qualified candidates that match your technical requirements, culture, and compliance needs — from startups to Fortune 500 companies.',
  features: ['Full-Stack & Frontend Developers', 'Cybersecurity & Cloud Specialists', 'AI/ML & Data Engineers', 'ERP & Salesforce Consultants', 'IT Project Managers & Architects', 'Systems & Solutions Architects'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11f75a879-1766554788471.png",
  alt: 'American technology professionals in a corporate office environment discussing IT project requirements and AI solutions'
},
{
  id: 'ai-talent',
  title: 'AI & Emerging Tech Talent',
  description: 'Stay ahead of the curve with specialized AI, machine learning, and emerging technology talent. We have a dedicated pipeline of AI/ML engineers, data scientists, NLP specialists, and computer vision experts available across India and the USA.',
  features: ['Machine Learning Engineers', 'NLP & Computer Vision Specialists', 'Data Scientists & AI Researchers', 'Generative AI Developers', 'MLOps & AI Infrastructure Engineers', 'AI Product Managers'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d9401701-1785765497079.png",
  alt: 'AI and machine learning engineers working on neural network models and data science projects in a modern tech lab'
},
{
  id: 'contract',
  title: 'Contract Staffing',
  description: 'Flexible contract staffing solutions for short-term projects, seasonal demands, or specialized skills. Scale your team up or down quickly without the overhead of permanent hires — available across India and the USA.',
  features: ['Short-Term Project Staffing', 'Seasonal Workforce Solutions', 'Specialized Skill Contracts', 'Contract-to-Hire Options', 'Rapid Deployment (48-72 hrs)', 'Compliance & Payroll Management'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e7e0f6e6-1771505683403.png",
  alt: 'Contract IT consultant meeting with client team to discuss project scope and technical requirements'
},
{
  id: 'permanent',
  title: 'Permanent Placement',
  description: 'Find your next long-term IT team member. Our permanent placement service identifies, screens, and presents candidates who not only have the right skills but also align with your company culture and values — with a 90-day replacement guarantee.',
  features: ['Executive IT Search', 'Technical Skill Assessment', 'Cultural Fit Evaluation', 'Background Verification', 'Offer Negotiation Support', '90-Day Replacement Guarantee'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dd7e75a9-1766824954462.png",
  alt: 'HR manager conducting a permanent placement interview with a software engineering candidate'
},
{
  id: 'augmentation',
  title: 'Staff Augmentation',
  description: 'Extend your existing team with skilled IT professionals who integrate seamlessly into your workflow. Our staff augmentation model gives you the flexibility to scale without the complexity of traditional hiring — on-site or remote, India or USA.',
  features: ['Team Extension Services', 'On-Site & Remote Options', 'Seamless Team Integration', 'Dedicated Resource Model', 'Flexible Engagement Terms', 'Knowledge Transfer Support'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_113d56666-1773207076522.png",
  alt: 'Staff augmentation team members joining a video conference call with the client development team'
}];


const techSkills = [
'React / Next.js', 'Node.js / Python', 'Java / Spring Boot', 'AWS / Azure / GCP',
'DevOps / Kubernetes', 'Machine Learning / AI', 'Generative AI / LLMs', 'Data Science',
'Salesforce / SAP', 'Cybersecurity', 'Mobile (iOS/Android)', 'QA Automation'];


export default function ITStaffingPage() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animated');
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <Topbar />
      <Header />
      {/* Page Hero */}
      <section className="page-hero-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-2 mb-5">
                <span className="text-primary text-sm font-semibold font-lato tracking-wide">PILLAR 2 OF 3 — INDIA & USA</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white font-poppins leading-tight mb-4">
                IT Staffing
                <span className="block text-primary text-3xl md:text-4xl mt-2">India & USA Talent Solutions</span>
              </h1>
              <p className="text-white/70 font-lato text-lg leading-relaxed mb-8">
                We help companies find the right IT talent in India and the USA — from AI/ML engineers to full-stack developers, contract staffing to permanent placement. Your talent partner across two continents.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/contact" className="bbt-btn-primary">Find Talent Now</Link>
                <Link href="#services" className="bbt-btn-outline border-white/40 text-white hover:bg-white hover:text-primary">Our Services</Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1efe8e939-1772070605602.png"
                alt="IT staffing professionals reviewing candidate profiles and technical assessments in a modern recruitment office"
                className="rounded-lg shadow-2xl" />
              
            </div>
          </div>
        </div>
      </section>

      {/* India & USA Flags Banner */}
      <section className="py-8 bg-gradient-to-r from-indigo-900 to-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="flex items-center gap-4">
              <span className="text-4xl">🇮🇳</span>
              <div>
                <div className="text-white font-bold font-poppins text-lg">India Staffing</div>
                <div className="text-white/70 font-lato text-sm">5M+ IT Professionals · Cost-Effective · Fast Placement</div>
              </div>
            </div>
            <div className="w-px h-12 bg-white/20 hidden md:block"></div>
            <div className="flex items-center gap-4">
              <span className="text-4xl">🇺🇸</span>
              <div>
                <div className="text-white font-bold font-poppins text-lg">USA Staffing</div>
                <div className="text-white/70 font-lato text-sm">Local & Remote · Compliance-Ready · All Tech Stacks</div>
              </div>
            </div>
            <div className="w-px h-12 bg-white/20 hidden md:block"></div>
            <div className="flex items-center gap-4">
              <span className="text-4xl">🤖</span>
              <div>
                <div className="text-white font-bold font-poppins text-lg">AI/ML Talent</div>
                <div className="text-white/70 font-lato text-sm">Specialized AI Engineers · Data Scientists · LLM Experts</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bbt-stats-section py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
            { value: '1200+', label: 'Professionals Placed' },
            { value: '48hrs', label: 'Average Time to First CV' },
            { value: '95%', label: 'Placement Success Rate' },
            { value: '2', label: 'Countries: India & USA' }]?.map((stat, i) =>
            <div key={i}>
                <div className="text-4xl font-bold text-white font-poppins mb-2">{stat?.value}</div>
                <div className="text-white/70 text-sm font-lato">{stat?.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="bbt-section-title">IT STAFFING SERVICES</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3 mb-4"></div>
            <p className="bbt-section-subtitle">Comprehensive talent solutions for technology companies in India and the USA — including specialized AI/ML talent</p>
          </div>
          <div className="space-y-16">
            {staffingServices?.map((service, i) =>
            <div
              key={service?.id}
              id={service?.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center animate-on-scroll`}>
              
                <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-2xl font-bold text-text-main font-poppins">{service?.title}</h3>
                    {(service?.id === 'india' || service?.id === 'usa') &&
                  <span className="text-2xl">{service?.id === 'india' ? '🇮🇳' : '🇺🇸'}</span>
                  }
                    {service?.id === 'ai-talent' &&
                  <span className="text-2xl">🤖</span>
                  }
                  </div>
                  <p className="text-gray-600 font-poppins text-base leading-relaxed mb-6">{service?.description}</p>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {service?.features?.map((f, fi) =>
                  <div key={fi} className="flex items-center gap-2 text-sm text-gray-600 font-lato">
                        <svg className="w-4 h-4 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {f}
                      </div>
                  )}
                  </div>
                  <Link href="/contact" className="bbt-btn-primary text-sm">Get Started</Link>
                </div>
                <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                  <img src={service?.image} alt={service?.alt} className="w-full rounded-lg shadow-card-hover" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Tech Skills */}
      <section className="py-20" style={{ backgroundColor: '#6d97bf' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white font-poppins mb-3">TECHNOLOGIES & SKILLS WE STAFF</h2>
            <div className="w-16 h-1 bg-white/50 mx-auto"></div>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {techSkills?.map((skill, i) =>
            <span key={i} className="bg-white/10 border border-white/20 text-white px-5 py-2 rounded-full text-sm font-lato font-semibold hover:bg-white hover:text-primary transition-all cursor-default">
                {skill}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* India vs USA Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="bbt-section-title">INDIA VS USA STAFFING — WHICH IS RIGHT FOR YOU?</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3 mb-4"></div>
            <p className="bbt-section-subtitle">We help you make the right talent decision based on your project needs, budget, and timeline</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="animate-on-scroll bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="text-4xl">🇮🇳</span>
                <h3 className="text-xl font-bold text-text-main font-poppins">Hire in India — Best For:</h3>
              </div>
              <ul className="space-y-3">
                {[
                'Cost-effective talent (up to 60% savings vs US rates)',
                'Large team builds and offshore development centers',
                'AI/ML, data science, and software engineering roles',
                'Round-the-clock development and support coverage',
                'Startups and scale-ups building product teams',
                'Long-term dedicated resource engagements']?.
                map((item, i) =>
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600 font-lato">
                    <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {item}
                  </li>
                )}
              </ul>
              <div className="mt-6">
                <Link href="/contact" className="bbt-btn-primary text-sm">Hire in India</Link>
              </div>
            </div>
            <div className="animate-on-scroll bg-gradient-to-br from-red-50 to-orange-50 border border-red-100 rounded-lg p-8" style={{ animationDelay: '0.15s' }}>
              <div className="flex items-center gap-3 mb-5">
                <span className="text-4xl">🇺🇸</span>
                <h3 className="text-xl font-bold text-text-main font-poppins">Hire in USA — Best For:</h3>
              </div>
              <ul className="space-y-3">
                {[
                'On-site presence and local market expertise',
                'Compliance-sensitive roles requiring US citizenship/clearance',
                'Client-facing positions and executive leadership',
                'Cybersecurity, compliance, and regulatory roles',
                'Enterprises with US-only hiring policies',
                'Hybrid and on-site team requirements']?.
                map((item, i) =>
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600 font-lato">
                    <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {item}
                  </li>
                )}
              </ul>
              <div className="mt-6">
                <Link href="/contact" className="bbt-btn-primary text-sm">Hire in USA</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-secondary-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="bbt-section-title">OUR STAFFING PROCESS</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
            { step: '01', title: 'Understand Requirements', desc: 'We deep-dive into your technical needs, team culture, and project goals to define the ideal candidate profile — whether for India, USA, or AI/ML roles.' },
            { step: '02', title: 'Source & Screen', desc: 'We tap our network of 50,000+ IT professionals across India and the USA, rigorously screening candidates for technical skills, cultural fit, and AI/ML expertise.' },
            { step: '03', title: 'Present Top Candidates', desc: 'You receive a curated shortlist of pre-vetted candidates within 48 hours, complete with detailed profiles, technical assessments, and salary expectations.' },
            { step: '04', title: 'Onboard & Support', desc: 'We facilitate interviews, manage offers, handle compliance, and provide ongoing support to ensure a smooth onboarding experience in India or the USA.' }]?.map((step, i) =>
            <div key={i} className="bg-white p-6 rounded-lg shadow-card animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-4xl font-bold text-primary/20 font-poppins mb-3">{step?.step}</div>
                <h4 className="font-bold text-text-main font-lato text-sm uppercase tracking-wide mb-3">{step?.title}</h4>
                <p className="text-gray-600 text-sm font-poppins leading-relaxed">{step?.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bbt-cta-section rounded-lg">
            <h2 className="text-2xl font-bold text-text-main font-poppins mb-3 uppercase tracking-wide">LOOKING FOR IT TALENT IN INDIA OR USA?</h2>
            <p className="text-gray-600 font-poppins mb-8 text-lg">&ldquo;Tell us your requirements and we&apos;ll find the right talent within 48 hours — India, USA, or AI/ML specialists&rdquo;</p>
            <Link href="/contact" className="bbt-btn-primary">FIND TALENT NOW</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>);

}